import React, { useEffect } from "react";
import LayersPanel from "./LayersPanel";
import LibraryPanel from "./LibraryPanel";
import MediaPanel from "./MediaPanel";
import { NODE_LIST_CONTROLLER } from "../NodeList/NodeList";

const logSelectionDebug = (stage, payload) => {
  console.log(`[SelectionDebug][LeftSidebar] ${stage}`, payload);
};

const normalizeFeatureReferenceId = (value) => {
  if (value == null) {
    return null;
  }

  return String(value).trim();
};

const LeftSidebar = ({ 
  activeTab, 
  setActiveTab, 
  layersPanelProps,
  libraryItems,
  onLibraryDragStart,
  onLibraryDragEnd,
  onAddFromTemplate,
  onDeleteCampaignFeature,
  onSelectCampaignFeature,
  onHoverCampaignFeature,
  featuresList,

  leftListNewCustomisationItems,
  selectedFeatureId,
  selectedFeatureNode,
  selectedLayersNodeState,
  selectedFieldDataLink,
  setSelectedFeatureId,
  setSelectedFeatureNode,
  setSelectedLayersNodeState,
  activateNode,
  activeNode,
  clearActiveNode,
  setTimelinePlayheadStep,
  forceUpdateComponentFunc,

  currentMode,
  setCurrentMode,

  campaignData,
  customisationVars,
}) => {
  useEffect(() => {
    if (activeTab !== 'layers') {
      return undefined;
    }

    const hasChildSettingsSelection = Boolean(selectedLayersNodeState?.hierarchy?.length);
    const hasRootFeatureSelection = Boolean(
      selectedLayersNodeState?.featureId
      && !hasChildSettingsSelection
      && normalizeFeatureReferenceId(selectedLayersNodeState.featureId) === normalizeFeatureReferenceId(selectedFeatureId),
    );

    const targetNode = activeNode || null;
    const targetFeatureId = normalizeFeatureReferenceId(
      targetNode?.topNode?.id || targetNode?.featureData?._id || targetNode?.featureData?.id || targetNode?.id || selectedFeatureId,
    );
    const layers = layersPanelProps?.layers || [];
    const expandedLayers = layersPanelProps?.expandedLayers;
    const onLayerToggle = layersPanelProps?.onLayerToggle;

    if (!targetFeatureId || !expandedLayers || typeof onLayerToggle !== 'function') {
      return undefined;
    }

    const sectionsToExpand = new Set();

    layers.forEach((layer) => {
      if (!layer?.isComponent) {
        return;
      }

      if (normalizeFeatureReferenceId(layer.featureId) !== targetFeatureId) {
        return;
      }

      if (layer.parentStep) {
        sectionsToExpand.add(layer.parentStep);
        return;
      }

      sectionsToExpand.add('triggeredComponents');
    });

    const collapsedSections = Array.from(sectionsToExpand).filter(
      (sectionId) => !expandedLayers.has(sectionId),
    );

    if (collapsedSections.length > 0) {
      collapsedSections.forEach((sectionId) => {
        onLayerToggle(sectionId);
      });
      return undefined;
    }

    if (hasChildSettingsSelection || hasRootFeatureSelection || !targetNode?.id || !targetNode?.path) {
      return undefined;
    }

    const timer = setTimeout(() => {
      logSelectionDebug('programmatic-node-select', {
        targetNodeName: targetNode?.name,
        targetNodeId: targetNode?.id,
        targetNodePath: targetNode?.path,
        targetFeatureId,
        selectedLayersNodeState,
      });
      NODE_LIST_CONTROLLER.selectNodeByIdAndPath(
        'customisation-workspace-left-list',
        targetNode.id,
        targetNode.path,
        false,
        true,
        false,
      );
    }, 0);

    return () => clearTimeout(timer);
  }, [activeTab, activeNode, layersPanelProps, selectedFeatureId, selectedFeatureNode, selectedLayersNodeState]);

  return (
    <div className="timeline-editor-col library">
      <div className="sidebar-tabs">
        <button 
          className={`tab-button ${activeTab === 'layers' ? 'active' : ''}`}
          onClick={() => setActiveTab('layers')}
        >
          Layers
        </button>
        <button 
          className={`tab-button ${activeTab === 'library' ? 'active' : ''}`}
          onClick={() => setActiveTab('library')}
        >
          Library
        </button>
        <button 
          className={`tab-button ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Media
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'layers' && (
          <LayersPanel 
            {...layersPanelProps} 
            leftListNewCustomisationItems={leftListNewCustomisationItems}
            selectedFeatureId={selectedFeatureId}
            selectedLayersNodeState={selectedLayersNodeState}
            selectedFieldDataLink={selectedFieldDataLink}
            setSelectedFeatureId={setSelectedFeatureId}
            setSelectedFeatureNode={setSelectedFeatureNode}
            setSelectedLayersNodeState={setSelectedLayersNodeState}
            activateNode={activateNode}
            activeNode={activeNode}
            clearActiveNode={clearActiveNode}
            setTimelinePlayheadStep={setTimelinePlayheadStep}
            forceUpdateComponentFunc={forceUpdateComponentFunc}
            campaignData={campaignData}
            libraryItems={libraryItems}
          />
        )}
        
        {activeTab === 'library' && (
          <LibraryPanel 
            libraryItems={libraryItems}
            onDragStart={onLibraryDragStart}
            onDragEnd={onLibraryDragEnd}
            onAddFromTemplate={onAddFromTemplate}
            onDeleteCampaignFeature={onDeleteCampaignFeature}
            onSelectCampaignFeature={onSelectCampaignFeature}
            onHoverCampaignFeature={onHoverCampaignFeature}
            featuresList={featuresList}
            selectedFeatureId={selectedFeatureId}
            campaignData={campaignData}
            customisationVars={customisationVars}
            leftListNewCustomisationItems={leftListNewCustomisationItems}
          />
        )}
        
        {activeTab === 'media' && (
          <MediaPanel campaignData={campaignData} />
        )}
      </div>
    </div>
  );
};

export default LeftSidebar;
